// import { StepLayoutChildProps, Step, useSteps, UseStepContext } from '@consigli/facade';
import { useMyUser, useCreateProjectMutation } from '@consigli/hooks';
import { isSharedOracleProject, Project } from '@consigli/types';
import { useCallback, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';

import { Breadcrumb } from '@/features/breadcrumb/breadcrumb';
import { CenteredLayout } from '@/layouts/centered-content';
import { FluidLayout } from '@/layouts/fluid-content';
import { Nav } from '@/layouts/nav';
import { Route } from '@/routes';
import { ProjectInputs } from '@/util/types';

import { ProjectDetails } from './project-details';
import { duplicateNameMessage, isDuplicateName } from './utils/duplicate-name';
import { incrementNameSuffix } from './utils/project-name';

// type TypeOfStepIndicatorRef = {
//   getStepIndicatorMethods: () => {
//     next: () => void;
//     hasNext: boolean;
//     previous: () => void;
//     hasPrevious: boolean;
//     step: number;
//   };
// };

export const ProjectCreation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    getValues,
    setValue,
    setFocus,
    setError,
  } = useForm<ProjectInputs>({ mode: 'onBlur' });
  const { user } = useMyUser();
  const [createProject, { isLoading }] = useCreateProjectMutation();
  // Holds the original/suggested names after a shared-project name clash so
  // the form can surface an inline message offering both choices.
  const [nameClash, setNameClash] = useState<{ original: string; suggested: string } | null>(null);

  // const stepIndicatorRef = useRef<TypeOfStepIndicatorRef | null>(null);

  const createNewProject = useCallback(async () => {
    const {
      name,
      address,
      country,
      specifications,
      constructionTypes,
      invoiceTo,
      orgNumber,
      billingAddress,
      billingInformation,
      oracleProjectNumber,
      oracleProjectTypeClassCode,
    } = getValues();
    if (!user.organizationId) {
      toast.error(`${t('project-creation.not-belong-to-organisation')}`);
      throw new Error('Cannot create new project without belonging to an organization!');
    }
    setNameClash(null);
    try {
      if (name) {
        const project: Project = await createProject({
          organizationId: user.organizationId,
          name,
          address,
          country,
          specifications,
          constructionTypes,
          invoiceTo,
          billingInformation: billingInformation ? billingInformation : null,
          orgNumber: orgNumber ? orgNumber : null,
          billingAddress: billingAddress ? billingAddress : null,
          oracleId: oracleProjectNumber ? oracleProjectNumber : null,
          availableServicesOverride: [],
        }).unwrap();
        toast.success(`${t('project-creation.success', { projectName: project.name })}`);
        // The tabbed page, not the service list: the /services index only
        // forwards here under the flag, and forwards nowhere without it.
        navigate(`/${Route.PROJECTS}/${project.id}/${Route.AGENTS}`);
      }
    } catch (error: unknown) {
      const duplicate = isDuplicateName(error);

      // Indirect and Bid & Proposal project numbers are shared by many users,
      // so the same Oracle name collides. Suggest a unique name and surface an
      // inline message so the user can either accept it or type their own,
      // instead of blocking them. Direct projects keep the exact Oracle name,
      // so a collision means the project already exists.
      if (duplicate && name && isSharedOracleProject(oracleProjectTypeClassCode)) {
        const suggestedName = incrementNameSuffix(name);
        setValue('name', suggestedName, { shouldValidate: true });
        setNameClash({ original: name, suggested: suggestedName });
        setFocus('name', { shouldSelect: true });
        toast.info(
          `${t('project-creation.project-name-suggested-unique', { original: name, suggested: suggestedName })}`,
        );
        return;
      }

      if (!duplicate) {
        toast.error(t('project-creation.failure'));
        return;
      }

      const message = duplicateNameMessage(error, t);
      setError('name', { type: 'server', message });
      setFocus('name');
      toast.error(message);
    }
  }, [getValues, user.organizationId, createProject, setValue, setFocus, setError, t, navigate]);
  const onSubmit: SubmitHandler<ProjectInputs> = useCallback(
    async () => {
      // if (stepIndicatorRef && stepIndicatorRef.current) {
      //   const { hasNext, next } = stepIndicatorRef?.current?.getStepIndicatorMethods();
      //   if (hasNext) {
      //     next();
      //   } else {
      await createNewProject();
    }, // remove this comma when un commenting the rest!
    // }
    //},
    [createNewProject],
  );
  // const { withStep, StepIndicator, StepOutlet, useStepContext } = useSteps(
  //   (step: (name: string, component: FC<StepLayoutChildProps>) => Step) => {
  //     return
  //       step(t('project-creation.project-details'), () => (
  //         <ProjectDetails
  //           ref={stepIndicatorRef}
  //           register={register}
  //           errors={errors}
  //           control={control}
  //         />
  //       )),
  //       step(t('project-creation.billing-information'), () => (
  //         <BillingInfo
  //           ref={stepIndicatorRef}
  //           register={register}
  //           errors={errors}
  //           control={control}
  //           getValues={getValues}
  //           context={useStepContext}
  //         />
  //       )),
  //     ;
  //   },
  // );

  const onBackClick = useCallback(() => {
    // if (stepIndicatorRef && stepIndicatorRef.current) {
    //   const { hasPrevious, previous } = stepIndicatorRef?.current?.getStepIndicatorMethods();
    //   if (hasPrevious) {
    //     previous();
    //   } else {
    navigate(`/${Route.PROJECTS}`);
  }, [navigate]);
  return (
    <FluidLayout>
      <Nav handleClick={onBackClick}></Nav>
      <Breadcrumb label={t('navbar.breadcrumbs.home')} to={`/${Route.PROJECTS}`} />
      <CenteredLayout>
        <h1 className="mt-12 mb-10 w-fit mx-auto pb-1 lg:pb-0 font-semibold text-xl text-center text-day-neutral-dark border-b-[3px] border-day-neutral-dark">
          {t('project-creation.project-details')}
        </h1>
        <form
          className="flex flex-col flex-1 items-center px-4 py-2 w-full sm:w-[30rem] mx-auto"
          onSubmit={handleSubmit(onSubmit)}
        >
          {/* <StepIndicator />
            <StepOutlet /> */}
          <ProjectDetails
            register={register}
            errors={errors}
            control={control}
            getValues={getValues}
            setValue={setValue}
            isLoading={isLoading}
            nameClash={nameClash}
            onDismissNameClash={() => setNameClash(null)}
            // context={useStepContext}
          />
        </form>
      </CenteredLayout>
    </FluidLayout>
  );
};
